import {
  Card,
  Flex,
  CardContent,
  Row,
  Division,
  Skeleton,
  Markdown,
  Span,
} from "@ir/ui";
import DrawerInfoCol from "./DrawerInfoColumn";
import { Deduction } from "@/core/entities/deduction";
import { useDeductionDrawer, useDeductionEdit } from "@/presentation/deduction/hooks";
import { DeductionInformationLabels } from "./utils/deductionInformationLabel.enum";
import DeductionStep from "./DeductionStep";
import { DeductionIntelligenceIcon } from "@/presentation/deduction/components";
import { useMemo } from "react";
import EditDeductionIntelligence from "./edit/EditDeductionIntelligence";
import { DeductionEditMode } from "@/store/deduction/deduction";
import DeductionDrawerActions from "../DeductionDrawerActions";

const DeductionDetailsInformation = ({ deduction }: { deduction: Deduction }) => {
  return [
    { label: DeductionInformationLabels.Status, value: deduction?.status?.statusName, span: 0, md: 0 },
    {
      label: DeductionInformationLabels.Priority,
      value: deduction?.priorityKey,
      name: deduction?.priorityName,
      span: 0,
      md: 0,
    },
    {
      label: DeductionInformationLabels.Validity,
      value: deduction?.validityKey,
      name: deduction?.validityName,
      span: 0,
      md: 0,
    },
  ];
};

/** Analysis Notes: Only show when deduction is invalid*/
const DeductionRcaDescription = ({
  value,
  isMarkdown = false,
  isInvalidDeduction = true,
}: {
  value: string;
  isMarkdown?: boolean;
  isInvalidDeduction?: boolean;
}) => {
  return (
    isInvalidDeduction && (
      <Division className="pt-4">
        <Span className="font-medium text-grey-600 text-xs">Analysis Notes:</Span>
        <Span className="font-medium text-primary-900 text-sm">
          {" "}
          {isMarkdown ? <Markdown content={value} /> : value}
        </Span>
      </Division>
    )
  );
};

const DeductionIntelligence = ({ deduction }: { deduction: Deduction }) => {
  const { isDeductionsLoading } = useDeductionDrawer();
  const { editMode } = useDeductionEdit();

  const isEditingIntelligence = useMemo(
    () => !isDeductionsLoading && editMode === DeductionEditMode.INTELLIGENCE,
    [editMode, isDeductionsLoading]
  );

  const hasAnyActionButton = Boolean(deduction?.id);

  return (
    <Card className="bg-white mb-3">
      <CardContent>
        <Division className="intelligence-info-content w-100">         
          {isEditingIntelligence ? (
            <EditDeductionIntelligence deduction={deduction} />
          ) : (
            <Flex direction="column">
              <Flex className="intelligence-info-row">
                <Division style={{ minWidth: "310px" }}>
                  <DeductionStep deduction={deduction} isLoading={isDeductionsLoading} />
                </Division>
                <Division className="intelligence-info-column">
                  <Row className="flex-1">
                    {DeductionDetailsInformation({ deduction }).map(item => (
                      <DrawerInfoCol
                        key={item.label}
                        label={item.label}
                        value={item.value}
                        name={item.name}
                        isLoading={isDeductionsLoading}
                        deductionSubTypeHelpText={deduction?.deductionSubTypeHelpText}
                        span={item.span}
                        md={item.md}
                        deduction={deduction}
                      />
                    ))}
                  </Row>
                </Division>
                <Division className="intelligence-info-column-insights">
                  {deduction?.deductionNumber && !isDeductionsLoading && <DeductionIntelligenceIcon />}
                </Division>
              </Flex>
            </Flex>
          )}
           {hasAnyActionButton && (
            <Flex justifyContent="end" align="end" direction="column" gap={0.5} wrap="no-wrap">
              {!isEditingIntelligence && <DeductionDrawerActions deduction={deduction} />}
            </Flex>
          )}
        </Division>
        {deduction?.rcaDescription && (
          <Flex>
            {isDeductionsLoading ? (
              <Skeleton className="mt-4" height={8} width={500} animation="wave" variant="rounded" />
            ) : (
              <DeductionRcaDescription
                value={deduction?.rcaDescription}
                isInvalidDeduction={deduction?.validityKey === "INVALID" || deduction?.validityKey === "PARTIALLY_INVALID"}
              />
            )}
          </Flex>
        )}
      </CardContent>
    </Card>
  );
};

export default DeductionIntelligence;
